import React, { useState, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Visibility as ViewIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
} from '@material-ui/icons'
import { Button, Box, Chip } from '@mui/material'
import {
  AdminPageWrapper,
  AdminSearchBar,
  AdminTable,
  AdminTableColumn,
  SearchFilters,
} from '../AdminComponents'
import { UserAdmin as UserAdminType } from '../../../../Generated/graphql-types'
import SEARCH_USERS from '../../../../Apollo/Queries/SearchUsers'

// Local interface for User Admin search filters
interface UserSearchFilters {
  id?: string
  email?: string
  name?: string
  nonprofit?: string
  funder?: string
}

export const UserAdmin: React.FC = () => {
  const history = useHistory()
  const location = useLocation()

  // Get filters from URL
  const params = new URLSearchParams(location.search)
  const nonprofitNameFromUrl = params.get('nonprofitName')
  const funderNameFromUrl = params.get('funderName')

  // Initialize search filters with URL params if present
  const [searchFilters, setSearchFilters] = useState<UserSearchFilters>(() => {
    const filters: UserSearchFilters = {}
    if (nonprofitNameFromUrl) {
      filters.nonprofit = nonprofitNameFromUrl
    }
    if (funderNameFromUrl) {
      filters.funder = funderNameFromUrl
    }
    return filters
  })

  // Pagination state
  const [page, setPage] = useState(0)
  const [pageSize] = useState(20)

  // Build query variables
  const queryVariables = useMemo(
    () => ({
      id: searchFilters.id ? parseInt(searchFilters.id, 10) : undefined,
      email: searchFilters.email || undefined,
      name: searchFilters.name || undefined,
      nonprofitName: searchFilters.nonprofit || undefined,
      funderName: searchFilters.funder || undefined,
      limit: pageSize,
      offset: page * pageSize,
    }),
    [searchFilters, page, pageSize]
  )

  // Check if at least one filter is applied (excluding pagination params)
  const hasActiveFilters = useMemo(() => {
    const { id, email, name, nonprofitName, funderName } = queryVariables
    return [id, email, name, nonprofitName, funderName].some(
      (value) => value !== undefined && value !== ''
    )
  }, [queryVariables])

  const { data, loading, error } = useQuery<{
    searchUsers: { users: UserAdminType[]; total: number }
  }>(SEARCH_USERS, {
    variables: queryVariables,
    skip: !hasActiveFilters, // Skip query if no filters are applied
  })

  const searchFields = [
    {
      key: 'id',
      label: 'User ID',
      placeholder: 'Enter user ID',
      type: 'number' as const,
    },
    { key: 'email', label: 'Email', placeholder: 'Enter email address' },
    { key: 'name', label: 'Name', placeholder: 'Enter first or last name' },
    {
      key: 'nonprofit',
      label: 'Nonprofit',
      placeholder: 'Enter nonprofit name',
    },
    { key: 'funder', label: 'Funder', placeholder: 'Enter funder name' },
  ]

  const columns: AdminTableColumn<UserAdminType>[] = [
    {
      key: 'id',
      label: 'ID',
      render: (value) => `#${value}`,
    },
    {
      key: 'email',
      label: 'Email',
    },
    {
      key: 'firstName',
      label: 'First Name',
    },
    {
      key: 'lastName',
      label: 'Last Name',
    },
    {
      key: 'disabled',
      label: 'Status',
      render: (value) => (
        <Chip
          label={value ? 'Disabled' : 'Active'}
          size='small'
          color={value ? 'default' : 'success'}
        />
      ),
      sortable: true,
    },
    {
      key: 'isTest',
      label: 'Type',
      render: (value) => (
        <Chip
          label={value ? 'Test' : 'Production'}
          size='small'
          color={value ? 'warning' : 'primary'}
        />
      ),
      sortable: true,
    },
    {
      key: 'userType',
      label: 'User Type',
      render: (_, user) => {
        const isFunder = user.funderOrgs && user.funderOrgs.length > 0
        const isNonprofit = user.nonprofitOrgs && user.nonprofitOrgs.length > 0

        if (isFunder && isNonprofit) {
          return <Chip label='Both' size='small' color='secondary' />
        }
        if (isFunder) {
          return <Chip label='Funder' size='small' color='info' />
        }
        if (isNonprofit) {
          return <Chip label='Nonprofit' size='small' color='primary' />
        }
        return <Chip label='None' size='small' />
      },
    },
  ]

  const users = useMemo(
    () => data?.searchUsers?.users?.filter(Boolean) || [],
    [data?.searchUsers]
  )

  const totalUsers = data?.searchUsers?.total || 0

  const handleRowClick = (user: UserAdminType) => {
    history.push(`/internal/self-serve/user-admin/${user.id}`)
  }

  const handleSearch = React.useCallback((filters: UserSearchFilters) => {
    setSearchFilters(filters)
    setPage(0) // Reset to first page when search filters change
  }, [])

  const clearUrlFilters = () => {
    history.push('/internal/self-serve/user-admin')
    setSearchFilters((prev) => {
      const newFilters = { ...prev }
      delete newFilters.nonprofit
      delete newFilters.funder
      return newFilters
    })
  }

  if (error) {
    return (
      <AdminPageWrapper title='User Administration'>
        <div>Error loading users: {error.message}</div>
      </AdminPageWrapper>
    )
  }

  const getSubtitle = () => {
    if (nonprofitNameFromUrl && funderNameFromUrl) {
      return `Viewing users in nonprofit: ${nonprofitNameFromUrl} and funder: ${funderNameFromUrl}`
    }
    if (nonprofitNameFromUrl) {
      return `Viewing users associated with nonprofit: ${nonprofitNameFromUrl}`
    }
    if (funderNameFromUrl) {
      return `Viewing users associated with funder: ${funderNameFromUrl}`
    }
    return 'View and manage all users in the system'
  }

  return (
    <AdminPageWrapper
      title='User Administration'
      subtitle={getSubtitle()}
      actions={
        <Box display='flex' gap={2}>
          <Button
            variant='contained'
            size='small'
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/internal/self-serve')}
            style={{
              backgroundColor: '#6B46C1',
              color: 'white',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Back to Self Serve
          </Button>
          {(nonprofitNameFromUrl || funderNameFromUrl) && (
            <Button
              variant='outlined'
              size='small'
              startIcon={<ClearIcon />}
              onClick={clearUrlFilters}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      }
    >
      <AdminSearchBar
        fields={searchFields}
        onSearch={handleSearch}
        initialFilters={searchFilters as SearchFilters}
      />

      {!hasActiveFilters ? (
        <Box
          p={4}
          textAlign='center'
          style={{
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            margin: '20px 0',
          }}
        >
          <h3>Please enter search criteria</h3>
          <p>
            Use the search filters above to find users by ID, email, name,
            nonprofit, or funder.
          </p>
        </Box>
      ) : (
        <AdminTable<UserAdminType>
          columns={columns}
          data={users}
          loading={loading}
          onRowClick={handleRowClick}
          emptyMessage='No users found'
          pageSize={pageSize}
          totalCount={totalUsers}
          currentPage={page}
          onPageChange={setPage}
          serverSidePagination
          actions={[
            {
              icon: <ViewIcon />,
              tooltip: 'View details',
              onClick: handleRowClick,
            },
          ]}
        />
      )}
    </AdminPageWrapper>
  )
}
